// ** Modules
import React from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { getProviders, signIn, signOut, useSession } from 'next-auth/react';

// ** UI
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import UndoRoundedIcon from '@mui/icons-material/UndoRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { alpha, useTheme } from '@mui/material/styles';

// ** Components
import Link from '@/components/common/Link';
import MenuView from './MenuView';
import SearchView from './SearchView';
import OverlayIconButton from './OverlayIconButton';
import StrapiImageRenderer from '@/components/strapi/StrapiImageRenderer';
import { Icon } from '@/components/common/icons';

// ** Styles
import { srOnlySx, iconButtonBaseSx, TRANSITION_EASING, OVERLAY_DURATION_S } from './megaMenuOverlay.constants';

// ** Mocks
import { AP07_MEGA_MENU_VIEW } from '@/features/ap07/mocks/ap07.menu.mock';

// ** Types
import type { MegaMenuView, MegaMenuOverlayProps } from './megaMenuOverlay.types';
import { logoSwitcher } from '@/utils/ui/styleUtils';

// ─── Component ────────────────────────────────────────────────────────────────

const MegaMenuOverlay = ({
	host,
	open,
	onClose,
	logoAlt = 'Logo',
	emptyStateHeading,
	emptyStateContactLabel,
	emptyStateEmailLine,
	emptyStatePhoneLine,
	menuView = AP07_MEGA_MENU_VIEW,
	mockLoggedIn,
	showSearch = false,
	filterTypOptions,
	filterThemaOptions
}: MegaMenuOverlayProps) => {
	const theme = useTheme();
	const logoSrc = host ? logoSwitcher(host) : '/images/drafts/logo.png';
	const { data: session, status } = useSession();
	const shouldReduceMotion = useReducedMotion();
	const isLoggedIn = mockLoggedIn !== undefined ? mockLoggedIn : Boolean(session?.user);

	// ── Temporary Login Lock Logic ───────────────────────────────────────────
	const blockedLoginHost = 'tischlerinnung-duesseldorf.de';
	const isHostLoginBlocked = host?.toLowerCase().trim() === blockedLoginHost.toLowerCase().trim();

	// Strapi "Display Member Login Button" toggle (defaults to shown).
	const showMemberLoginButton = menuView.showMemberLoginButton ?? true;
	const isLoginDisabled = isHostLoginBlocked || !showMemberLoginButton;

	const handleMemberAuthClick = React.useCallback(async () => {
		if (mockLoggedIn !== undefined) return;
		if (status === 'loading') return;
		if (isLoggedIn) {
			signOut({ callbackUrl: '/' });
			return;
		}
		const providers = await getProviders();
		const first = providers ? Object.values(providers)[0] : undefined;
		if (first) signIn(first.id);
	}, [isLoggedIn, mockLoggedIn, status]);

	const dialogTitleId = React.useId();

	const closeButtonRef = React.useRef<HTMLButtonElement>(null);
	const triggerRef = React.useRef<HTMLElement | null>(null);
	const overlayRef = React.useRef<HTMLDivElement>(null);
	const scrollAreaRef = React.useRef<HTMLDivElement>(null);

	const [view, setView] = React.useState<MegaMenuView>('menu');
	const [menuReady, setMenuReady] = React.useState(true);
	const menuReadyRef = React.useRef(menuReady);
	menuReadyRef.current = menuReady;

	const headerShowsMenu = view === 'menu' && menuReady;
	const showsSearchChrome = view === 'search' || (view === 'menu' && !menuReady);

	const [iconSlotCompact, setIconSlotCompact] = React.useState(true);
	const viewRef = React.useRef(view);
	viewRef.current = view;

	React.useEffect(() => {
		if (open) {
			triggerRef.current = document.activeElement as HTMLElement;
			const timer = setTimeout(() => closeButtonRef.current?.focus(), 60);
			return () => clearTimeout(timer);
		}
		triggerRef.current?.focus();
		triggerRef.current = null;
	}, [open]);

	React.useEffect(() => {
		// `html { scrollbar-gutter: stable }` keeps the gutter reserved while
		// scroll is locked, so removing the scrollbar here doesn't shift the
		// page content or fixed elements (e.g. the app bar).
		document.body.style.overflow = open ? 'hidden' : '';
		return () => {
			document.body.style.overflow = '';
		};
	}, [open]);

	React.useEffect(() => {
		if (!open) {
			const timer = setTimeout(
				() => {
					setView('menu');
					setMenuReady(true);
					setIconSlotCompact(true);
				},
				(OVERLAY_DURATION_S + 0.1) * 1000
			);
			return () => clearTimeout(timer);
		}
	}, [open]);

	React.useLayoutEffect(() => {
		if (showsSearchChrome) setIconSlotCompact(false);
	}, [showsSearchChrome]);

	const handleIconToggleExitComplete = React.useCallback(() => {
		if (viewRef.current === 'menu' && menuReadyRef.current) {
			setIconSlotCompact(true);
		}
	}, []);

	React.useEffect(() => {
		if (!open) return;
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				onClose();
				return;
			}
			if (e.key !== 'Tab') return;

			const root = overlayRef.current;
			if (!root) return;

			const focusable = Array.from(
				root.querySelectorAll<HTMLElement>(
					'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
				)
			).filter((el) => !el.hasAttribute('aria-hidden') && el.offsetParent !== null);

			if (focusable.length === 0) {
				e.preventDefault();
				return;
			}

			const first = focusable[0];
			const last = focusable[focusable.length - 1];
			const active = document.activeElement as HTMLElement | null;

			if (e.shiftKey && active === first) {
				e.preventDefault();
				last.focus();
				return;
			}
			if (!e.shiftKey && active === last) {
				e.preventDefault();
				first.focus();
			}
		};
		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [open, onClose]);

	// ── Derived ────────────────────────────────────────────────────────────────

	const white = theme.palette.common.white;

	const overlayTransition = {
		duration: shouldReduceMotion ? 0 : OVERLAY_DURATION_S,
		ease: TRANSITION_EASING
	};

	const viewTransition = {
		duration: shouldReduceMotion ? 0 : 0.1,
		ease: TRANSITION_EASING
	};

	const iconToggleTransition = {
		duration: shouldReduceMotion ? 0 : 0.05,
		ease: TRANSITION_EASING
	};

	const handleOpenSearch = React.useCallback(() => {
		scrollAreaRef.current?.scrollTo({ top: 0, left: 0, behavior: 'auto' });
		setMenuReady(false);
		setView('search');
	}, []);

	const handleBackToMenu = React.useCallback(() => setView('menu'), []);

	// ── Render ─────────────────────────────────────────────────────────────────

	return (
		<motion.div
			ref={overlayRef}
			initial={false}
			animate={{
				clipPath: open ? 'inset(0 0 0% 0)' : 'inset(0 0 100% 0)',
				pointerEvents: open ? 'auto' : 'none'
			}}
			transition={overlayTransition}
			style={{
				position: 'fixed',
				top: 0,
				left: 0,
				// Span the full window width (incl. the reserved scrollbar gutter)
				// so no empty strip shows on the right while scroll is locked.
				width: '100vw',
				height: '100%',
				zIndex: 1600,
				backgroundColor: theme.palette.loginOverlayCta.main,
				display: 'flex',
				flexDirection: 'column',
				overflow: 'hidden'
			}}
			role="dialog"
			aria-modal={open ? true : undefined}
			aria-hidden={!open}
			aria-labelledby={dialogTitleId}
			inert={!open}
		>
			<Typography id={dialogTitleId} component="h2" sx={srOnlySx}>
				Navigation und Suche
			</Typography>

			{/* ── Top bar ── */}
			<Box
				sx={{
					flexShrink: 0,
					display: 'grid',
					width: '100%',
					gridTemplateColumns: {
						xs: 'minmax(0, 1fr) auto',
						md: headerShowsMenu ? 'minmax(0, 1fr) 168px minmax(0, 1fr)' : 'minmax(0, 1fr) auto'
					},
					gridTemplateRows: {
						xs: headerShowsMenu ? 'auto auto' : 'auto',
						md: 'auto'
					},
					columnGap: 0,
					rowGap: {
						xs: headerShowsMenu ? theme.spacing(13) : 0,
						md: 0
					},
					alignItems: 'center',
					px: {
						xs: theme.spacing(theme.layout.pagePx.xs),
						md: theme.spacing(theme.layout.pagePx.md),
						lg: theme.spacing(theme.layout.pagePx.lg)
					},
					pt: { xs: 3, md: 8 },
					pb: {
						xs: showsSearchChrome ? 14 : 6,
						md: 16
					}
				}}
			>
				{/* Logo */}
				<Box
					sx={{
						gridColumn: { xs: 1, md: 1 },
						gridRow: { xs: 1, md: 1 },
						position: 'relative',
						width: { xs: 60, md: 72 },
						height: { xs: 53, md: 64 },
						justifySelf: 'start',
						minWidth: 0
					}}
				>
					<Link
						href="/"
						sx={{
							position: 'relative',
							width: '100%',
							height: '100%'
						}}
					>
						<StrapiImageRenderer
							src={logoSrc}
							alt={logoAlt}
							fill
							loading="eager"
							style={{ objectFit: 'contain' }}
							sizes="(max-width: 900px) 72px, 81px"
						/>
					</Link>
				</Box>

				{/* Login / Logout (Hidden on beta.tischler.studio) */}
				{headerShowsMenu && !isLoginDisabled ? (
					<Box
						component="button"
						type="button"
						aria-label={isLoggedIn ? 'Logout' : 'Mitglieder-Login'}
						disabled={mockLoggedIn === undefined && status === 'loading'}
						onClick={handleMemberAuthClick}
						sx={{
							...iconButtonBaseSx,
							gridColumn: { xs: '1 / -1', md: 2 },
							gridRow: { xs: 2, md: 1 },
							justifySelf: { xs: 'stretch', md: 'center' },
							flexDirection: 'column',
							justifyInner: 'center',
							alignItems: 'center',
							width: { xs: '100%', md: '168px' },
							height: '48px',
							boxSizing: 'border-box',
							borderRadius: '2px',
							border: `1px solid ${alpha(white, 0.6)}`,
							fontFamily: '"DIN Next LT Pro"',
							fontSize: '18px',
							fontStyle: 'normal',
							fontWeight: 400,
							lineHeight: '24px',
							color: 'common.white',
							textAlign: 'center',
							transition: 'opacity 0.2s, border-color 0.2s',
							'&:hover': {
								borderColor: white
							},
							'&:focusVisible': {
								outline: `2px solid ${alpha(white, 0.55)}`,
								outlineOffset: 4
							},
							'&:disabled': {
								opacity: 0.5,
								cursor: 'not-allowed'
							}
						}}
					>
						{isLoggedIn ? 'Logout' : 'Mitglieder-Login'}
					</Box>
				) : null}

				{/* Right: search/back toggle + close */}
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: 8,
						gridColumn: { xs: 2, md: headerShowsMenu ? 3 : 2 },
						gridRow: { xs: 1, md: 1 },
						justifySelf: 'end'
					}}
				>
					{showSearch ? (
						<Box
							sx={{
								position: 'relative',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								flexShrink: 0,
								...(iconSlotCompact ? { width: 36, height: 36 } : { minWidth: 36 })
							}}
						>
							<AnimatePresence mode="wait" initial={false} onExitComplete={handleIconToggleExitComplete}>
								{headerShowsMenu ? (
									<motion.div
										key="search-toggle"
										initial={{ opacity: 0 }}
										animate={{ opacity: 1 }}
										exit={{ opacity: 0 }}
										transition={iconToggleTransition}
										style={{
											position: 'absolute',
											inset: 0,
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center'
										}}
									>
										<OverlayIconButton
											ariaLabel="Suche öffnen"
											onClick={handleOpenSearch}
											sx={{
												opacity: 1,
												transition: 'opacity 0.2s',
												'&:hover': { opacity: 0.65 }
											}}
										>
											<Icon name="search" color={white} size={24} />
										</OverlayIconButton>
									</motion.div>
								) : (
									<motion.div
										key="back-toggle"
										initial={{ opacity: 0 }}
										animate={{ opacity: 1 }}
										exit={{ opacity: 0 }}
										transition={iconToggleTransition}
										style={{
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center'
										}}
									>
										<OverlayIconButton
											ariaLabel="Zurück zum Menü"
											onClick={handleBackToMenu}
											sx={{
												flexDirection: 'column',
												gap: 0.25,
												opacity: 1,
												transition: 'opacity 0.2s',
												'&:hover': { opacity: 0.65 }
											}}
										>
											<UndoRoundedIcon sx={{ fontSize: 28, color: white }} />
											<Typography
												component="span"
												variant="caption"
												sx={{
													fontSize: '0.6875rem',
													lineHeight: 1.15,
													fontWeight: 400,
													color: alpha(white, 0.82),
													textAlign: 'center',
													whiteSpace: 'nowrap',
													userSelect: 'none'
												}}
											>
												Zurück zum Menü
											</Typography>
										</OverlayIconButton>
									</motion.div>
								)}
							</AnimatePresence>
						</Box>
					) : null}

					<OverlayIconButton
						ref={closeButtonRef}
						ariaLabel="Schließen"
						onClick={onClose}
						sx={{
							width: 48,
							height: 48,
							borderRadius: '12px',
							backgroundColor: alpha(white, 0.12),
							transition: 'background-color 0.2s',
							'&:hover': {
								backgroundColor: alpha(white, 0.22)
							}
						}}
					>
						<CloseRoundedIcon sx={{ fontSize: 28, color: white }} />
					</OverlayIconButton>
				</Box>
			</Box>

			{/* ── Scroll container ── */}
			<Box
				ref={scrollAreaRef}
				sx={{
					flex: 1,
					minHeight: 0,
					overflowAnchor: 'none',
					overflowY: 'auto',
					overflowX: 'hidden',
					display: 'flex',
					flexDirection: 'column',
					[theme.breakpoints.up('md')]: {
						scrollbarWidth: 'thin',
						scrollbarColor: `${alpha(white, 0.45)} ${alpha(white, 0.1)}`,
						'&::-webkit-scrollbar': { width: 0 },
						'&::-webkit-scrollbar-track': {
							backgroundColor: alpha(white, 0.08),
							borderRadius: 5
						},
						'&::-webkit-scrollbar-thumb': {
							backgroundColor: alpha(white, 0.4),
							borderRadius: 5,
							border: '2px solid transparent',
							backgroundClip: 'content-box'
						},
						'&::-webkit-scrollbar-thumb:hover': {
							backgroundColor: alpha(white, 0.58)
						}
					},
					[theme.breakpoints.down('md')]: {
						scrollbarWidth: 'none',
						msOverflowStyle: 'none',
						'&::-webkit-scrollbar': { display: 'none' }
					}
				}}
			>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						position: 'relative',
						minHeight: '100%',
						px: {
							xs: theme.spacing(theme.layout.pagePx.xs),
							md: theme.spacing(theme.layout.pagePx.md),
							lg: theme.spacing(theme.layout.pagePx.lg)
						},
						pb: { xs: 6, md: 8 }
					}}
				>
					<AnimatePresence initial={false}>
						{view === 'menu' && menuReady && (
							<motion.div
								key="menu-content"
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								transition={{
									duration: shouldReduceMotion ? 0 : 0.12,
									ease: TRANSITION_EASING
								}}
							>
								<MenuView {...menuView} isLoggedIn={isLoggedIn} />
							</motion.div>
						)}
					</AnimatePresence>

					{showSearch ? (
						<AnimatePresence
							onExitComplete={() => {
								scrollAreaRef.current?.scrollTo({
									top: 0,
									left: 0,
									behavior: 'auto'
								});
								setMenuReady(true);
							}}
						>
							{view === 'search' && (
								<motion.div
									key="search-cover"
									initial={{ opacity: 1 }}
									animate={{ opacity: 1 }}
									exit={{ opacity: 0 }}
									transition={viewTransition}
									style={{
										position: 'absolute',
										top: 0,
										left: 0,
										right: 0,
										minHeight: '100%',
										backgroundColor: theme.palette.loginOverlayCta.main
									}}
								>
									<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={viewTransition}>
										<SearchView
											shouldReduceMotion={shouldReduceMotion ?? false}
											emptyStateHeading={emptyStateHeading}
											emptyStateContactLabel={emptyStateContactLabel}
											emptyStateEmailLine={emptyStateEmailLine}
											emptyStatePhoneLine={emptyStatePhoneLine}
											filterTypOptions={filterTypOptions}
											filterThemaOptions={filterThemaOptions}
										/>
									</motion.div>
								</motion.div>
							)}
						</AnimatePresence>
					) : null}
				</Box>
			</Box>
		</motion.div>
	);
};

export default MegaMenuOverlay;
